import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { productSevice } from "./productService";

export const getAllProducts = createAsyncThunk(
  "product/get",
  async (data, thunkAPI) => {
    try {
      return await productSevice.getProducts(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message || error.message || "Failed to load products",
        status: error.response?.status || 500
      });
    }
  }
);

export const getAProduct = createAsyncThunk(
  "product/getAProduct",
  async (id, thunkAPI) => {
    try {
      return await productSevice.getSingleProduct(id);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message || error.message || "Failed to load product",
        status: error.response?.status || 500
      });
    }
  }
);

export const addToWishlist = createAsyncThunk(
  "product/wishlist",
  async (prodId, thunkAPI) => {
    try {
      return await productSevice.addToWishlist(prodId);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message || error.message || "Failed to add to wishlist",
        status: error.response?.status || 500
      });
    }
  }
);

const productState = {
  product: [],
  totalProducts: 0,
  currentPage: 1,
  totalPages: 0,
  limit: 50, // Increased default limit
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

export const addRating = createAsyncThunk(
  "product/rating",
  async (data, thunkAPI) => {
    try {
      return await productSevice.rateProduct(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message || error.message || "Failed to add rating",
        status: error.response?.status || 500
      });
    }
  }
);

export const productSlice = createSlice({
  name: "product",
  initialState: productState,
  reducers: {
    resetProducts: (state) => {
      state.product = [];
      state.currentPage = 1;
      state.totalProducts = 0;
      state.totalPages = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const append = action.meta?.arg?.append;

        // Handle both paginated and non-paginated responses
        if (action.payload && typeof action.payload === 'object' && action.payload.products) {
          const incoming = action.payload.products || [];
          state.product = append ? [...state.product, ...incoming] : incoming;
          state.totalProducts = action.payload.totalProducts || 0;
          state.currentPage = action.payload.currentPage || 1;
          state.limit = action.payload.limit || 24;
          state.totalPages = Math.ceil(state.totalProducts / state.limit);
        } else {
          const incoming = action.payload || [];
          state.product = append ? [...state.product, ...incoming] : incoming;
          state.totalProducts = state.product.length;
          state.currentPage = 1;
          state.totalPages = 1;
        }
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload?.message || "Failed to load products";
      })
      .addCase(addToWishlist.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.addToWishlist = action.payload;
        state.message = "Product Added to Wishlist!";
        toast.success("Wishlist updated");
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload?.message || "Failed to add to wishlist";
        if (action.payload?.status === 401) {
          toast.error("Please login to add to wishlist");
        } else {
          toast.error(action.payload?.message || "Failed to update wishlist");
        }
      })
      .addCase(getAProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.singleproduct = action.payload;
        state.message = "Product Fetched Successfully";
      })
      .addCase(getAProduct.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload?.message || "Failed to load product";
      })
      .addCase(addRating.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addRating.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.rating = action.payload;
        state.message = "Rating Added Successfully";
        if (state.isSuccess) {
          toast.success("Rating Added Successfully");
        }
      })
      .addCase(addRating.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload?.message || "Failed to add rating";
      });
  },
});

export const { resetProducts } = productSlice.actions;
export default productSlice.reducer;
